import React, { useState, useEffect } from 'react';
import {
    AppBar,
    Toolbar,
    Button,
    Typography,
    Container,
    Grid,
    Card,
    CardContent,
    CardMedia,
    Tabs,
    Tab,
    Box,
    useTheme,
    useMediaQuery,
    IconButton,
    Drawer,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider,
    Fade,
    Zoom,
    Slide,
    Grow,
    alpha,
    useScrollTrigger,
    CssBaseline
} from '@mui/material';
import {
    Menu as MenuIcon,
    MobileFriendly as TopUpIcon,
    Tv as TelevisionIcon,
    Wifi as InternetIcon,
    FlashOn as ElectricityIcon,
    WaterDrop as KhanepaniIcon,
    Security as AntivirusIcon,
    AccountBalanceWallet as RechargeIcon,
    LocalAtm as InsuranceIcon,
    Login as LoginIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import { keyframes } from '@mui/system';
import { antivirus, electricity, insurance, internet, khanepani, recharge, television, topup } from './ServiceList';
import { useNavigate } from 'react-router-dom';
import logo from '../../images/espayLogo.png'

// Floating animation for cards
const floatAnimation = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

// Gradient background animation
const gradientAnimation = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

// Custom component for scroll-triggered animations
function ElevationScroll(props) {
    const theme = useTheme();
    const { children } = props;
    const trigger = useScrollTrigger({
        disableHysteresis: true,
        threshold: 0,
    });

    return React.cloneElement(children, {
        elevation: trigger ? 4 : 0,
        sx: {
            transition: theme => theme.transitions.create(['box-shadow', 'background-color'], {
                duration: theme.transitions.duration.standard,
            }),
            backgroundColor: trigger ? alpha(theme.palette.primary.main, 0.9) : theme.palette.primary.main,
            backdropFilter: trigger ? 'blur(10px)' : 'none',
        }
    });
}

const Services = () => {
    const navigate = useNavigate();
    const [value, setValue] = useState(0);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [animate, setAnimate] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    useEffect(() => {
        // Trigger animations after component mounts
        setAnimate(true);
    }, []);

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    // List of service tabs
    const serviceTabs = [
        { label: 'TopUp', icon: <TopUpIcon />, content: topup, title: 'Topup', description: 'User can make topup for different telecom services like NTC, NCELL, Smart Cell and many more via esPay.' },
        { label: 'Television', icon: <TelevisionIcon />, content: television, title: 'Television', description: 'User can pay tv bills for different vendor like Dish home, SIM TV, Clear TV and many more via esPay.' },
        { label: 'Internet', icon: <InternetIcon />, content: internet, title: 'Internet', description: 'User can pay Internet bills for different vendor like Worldlink, Classictech, Subisu, Vianet and many more via esPay.' },
        { label: 'Electricity', icon: <ElectricityIcon />, content: electricity, title: 'Electricity', description: 'User can pay electricity bills for different counter listed in Nepal Electricity Authority via esPay.' },
        { label: 'Khanepani', icon: <KhanepaniIcon />, content: khanepani, title: 'Khanepani', description: 'User can pay khanepani bills for different counter listed in Nepal Khanepani Sanasthan via esPay.' },
        { label: 'Antivirus', icon: <AntivirusIcon />, content: antivirus, title: 'Antivirus', description: 'User can purchase antivirus product like Kaspersky, Eset, Wardwiz, Mcafee and many more via esPay.' },
        { label: 'Recharge', icon: <RechargeIcon />, content: recharge, title: 'Recharge', description: 'User can purchase recharge card for NTC, NCELL, BroadLink, Dish home via esPay.' },
        { label: 'Insurance', icon: <InsuranceIcon />, content: insurance, title: 'Insurance', description: 'User can pay insurance bill for different vendor like Ajod, Genral, Everest, Sanima General and many more via esPay.' },
    ];

    // Drawer content for mobile
    const drawer = (
        <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center' }}>
            <Box sx={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2,
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
            }}>
                <Typography variant="h6">
                    esPay Services
                </Typography>
                <IconButton sx={{ color: theme.palette.primary.contrastText }}>
                    <CloseIcon />
                </IconButton>
            </Box>
            <Divider />
            <List>
                {serviceTabs.map((tab, index) => (
                    <ListItem
                        button
                        key={index}
                        selected={value === index}
                        onClick={(e) => handleChange(e, index)}
                        sx={{
                            overflowY: 'auto',
                            '&.Mui-selected': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                borderRight: `4px solid ${theme.palette.primary.main}`
                            }
                        }}
                    >
                        <ListItemIcon sx={{ color: value === index ? 'primary.main' : 'inherit' }}>
                            {tab.icon}
                        </ListItemIcon>
                        <ListItemText
                            primary={tab.label}
                            primaryTypographyProps={{
                                fontWeight: value === index ? 'bold' : 'normal'
                            }}
                        />
                    </ListItem>
                ))}
            </List>
        </Box>
    );

    return (
        <>
            <ElevationScroll>
                <AppBar position="sticky" color="primary" elevation={2} sx={{
                    background: `linear-gradient(270deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                    backgroundSize: '200% 200%',
                    animation: `${gradientAnimation} 8s ease infinite`,
                }}>
                    <Toolbar>
                        {isMobile && (
                            <IconButton
                                color="inherit"
                                aria-label="open drawer"
                                edge="start"
                                onClick={handleDrawerToggle}
                                sx={{ mr: 2 }}
                            >
                                <MenuIcon />
                            </IconButton>
                        )}
                        <Slide in={animate} direction="right" timeout={800}>
                            {/* <img src={logo} alt="esPay Logo" style={{ height: '100px', marginRight: '10px' }} /> */}
                            <Box
                                component='img'
                                src={logo}
                                alt="esPay Logo"
                                style={{ height: '70px', marginRight: '10px' }}

                            />
                        </Slide>
                        <Slide in={animate} direction="right" timeout={800}>
                            <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
                                {/* esPay */}
                            </Typography>
                        </Slide>
                        <Slide in={animate} direction="left" timeout={800}>
                            <Button
                                variant="outlined"
                                color="inherit"
                                target='_blank'
                                href='/login'
                                startIcon={<LoginIcon />}
                                sx={{
                                    borderRadius: 2,
                                    px: 2,
                                    py: 1,
                                    transition: 'all 0.3s',
                                    '&:hover': {
                                        color: theme.palette.primary.contrastText,
                                        backgroundColor: alpha(theme.palette.primary.contrastText, 0.1),
                                        transform: 'translateY(-2px)'
                                    }
                                }}
                            >
                                Login
                            </Button>
                        </Slide>
                    </Toolbar>
                </AppBar>
            </ElevationScroll>

            <Box sx={{
                background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%)',
                minHeight: '100vh',
                py: 4
            }}>
                <Container maxWidth="xl">
                    <Grid container spacing={3}>
                        {/* Sidebar/Navigation - List Alternative */}
                        {!isMobile && (
                            <Grid item md={3}>
                                <Fade in={animate} timeout={1000}>
                                    <Box sx={{
                                        position: 'sticky',
                                        top: 100,
                                        borderRadius: 2,
                                        p: 2,
                                        backgroundColor: 'white',
                                        boxShadow: '0 8px 32px rgba(0,0,0,0.05)',
                                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                                    }}>
                                        <List>
                                            {serviceTabs.map((tab, index) => (
                                                <ListItem
                                                    button
                                                    key={index}
                                                    selected={value === index}
                                                    onClick={(e) => handleChange(e, index)}
                                                    sx={{
                                                        borderRadius: 1,
                                                        mb: 0.5,
                                                        py: 1.5,
                                                        transition: 'all 0.2s',
                                                        '&.Mui-selected': {
                                                            backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                                            color: theme.palette.primary.main,
                                                            fontWeight: 'bold',
                                                            borderLeft: `3px solid ${theme.palette.primary.main}`
                                                        },
                                                        '&:hover': {
                                                            backgroundColor: alpha(theme.palette.primary.main, 0.04),
                                                        }
                                                    }}
                                                >
                                                    <ListItemIcon sx={{
                                                        minWidth: 40,
                                                        color: value === index ? 'primary.main' : 'inherit'
                                                    }}>
                                                        {tab.icon}
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={tab.label}
                                                        primaryTypographyProps={{
                                                            fontWeight: value === index ? 'bold' : 'normal'
                                                        }}
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Box>
                                </Fade>
                            </Grid>
                        )}

                        {/* Mobile drawer */}
                        <Drawer
                            variant="temporary"
                            open={mobileOpen}
                            onClose={handleDrawerToggle}
                            ModalProps={{
                                keepMounted: true,
                            }}
                            sx={{
                                display: { xs: 'block', md: 'none' },
                                '& .MuiDrawer-paper': {
                                    boxSizing: 'border-box',
                                    width: 280,
                                    boxShadow: '4px 0 20px rgba(0,0,0,0.1)'
                                },
                            }}
                        >
                            {drawer}
                        </Drawer>

                        {/* Content Area */}
                        <Grid item xs={12} md={9}>
                            <Box sx={{
                                borderRadius: 2,
                                backgroundColor: 'white',
                                boxShadow: '0 8px 32px rgba(0,0,0,0.05)',
                                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                height: 'calc(100vh - 120px)',
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden'
                            }}>
                                <Grow in={animate} timeout={800}>
                                    <Box sx={{
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column'
                                    }}>
                                        {/* Dense Sticky Header Section */}
                                        <Box sx={{
                                            p: { xs: '8px 12px', sm: '12px 16px' },
                                            backgroundColor: 'white',
                                            position: 'sticky',
                                            top: 0,
                                            zIndex: 10,
                                            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                            backdropFilter: 'blur(8px)',
                                            background: alpha('#ffffff', 0.9)
                                        }}>
                                            <Typography variant="h5" gutterBottom sx={{
                                                fontWeight: 'bold',
                                                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                                backgroundClip: 'text',
                                                WebkitBackgroundClip: 'text',
                                                color: 'transparent',
                                                mb: 0.5,
                                                fontSize: { xs: '1.4rem', sm: '1.6rem', md: '1.8rem' },
                                                lineHeight: 1.2
                                            }}>
                                                {serviceTabs[value].title}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{
                                                mb: 0,
                                                lineHeight: 1.3,
                                                fontSize: { xs: '0.8rem', sm: '0.9rem' }
                                            }}>
                                                {serviceTabs[value].description}
                                            </Typography>
                                        </Box>

                                        {/* Scrollable Content Section */}
                                        <Box sx={{
                                            p: { xs: 1, sm: 2 },
                                            pt: 1,
                                            overflowY: 'auto',
                                            flex: 1,
                                            '&::-webkit-scrollbar': {
                                                width: '8px',
                                            },
                                            '&::-webkit-scrollbar-track': {
                                                background: alpha(theme.palette.grey[300], 0.3),
                                                borderRadius: '4px',
                                            },
                                            '&::-webkit-scrollbar-thumb': {
                                                background: alpha(theme.palette.primary.main, 0.4),
                                                borderRadius: '4px',
                                                '&:hover': {
                                                    background: alpha(theme.palette.primary.main, 0.6),
                                                }
                                            }
                                        }}>
                                            <Grid container spacing={2}>
                                                {serviceTabs[value].content.map((item, index) => (
                                                    <Grid item xs={6} sm={4} md={3} key={index}>
                                                        <Zoom in={animate} timeout={800 + (index * 100)}>
                                                            <Card
                                                                sx={{
                                                                    height: '100%',
                                                                    display: 'flex',
                                                                    flexDirection: 'column',
                                                                    transition: 'all 0.3s ease-in-out',
                                                                    borderRadius: 2,
                                                                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                                                    '&:hover': {
                                                                        transform: 'translateY(-8px)',
                                                                        boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
                                                                        animation: `${floatAnimation} 2s ease-in-out infinite`,
                                                                    }
                                                                }}
                                                            >
                                                                <CardMedia
                                                                    component="img"
                                                                    sx={{
                                                                        height: 120,
                                                                        objectFit: 'contain',
                                                                        p: 2,
                                                                        backgroundColor: alpha(theme.palette.primary.light, 0.05),
                                                                        transition: 'all 0.3s',
                                                                    }}
                                                                    image={item.image}
                                                                    alt={item.title}
                                                                />
                                                                <CardContent sx={{
                                                                    flexGrow: 1,
                                                                    textAlign: 'center',
                                                                    py: 2,
                                                                    '&:last-child': { pb: 2 }
                                                                }}>
                                                                    <Typography variant="body1" component="p" fontWeight="medium">
                                                                        {item.title}
                                                                    </Typography>
                                                                </CardContent>
                                                            </Card>
                                                        </Zoom>
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        </Box>
                                    </Box>
                                </Grow>
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>
        </>
    );
};

export default Services;